<script setup>
import { computed, inject } from 'vue';
import { initials, shortDate, styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

const post = inject('tiqCurrentPost', null);
const inCanvas = inject('tiqEditorCanvas', false);

// The design's byline: a mono date line over the author row, ruled top
// and bottom. Live, an empty byline renders nothing — in the editor
// canvas it shows placeholder names, or the slot would be an invisible
// block somebody has to guess the position of.
const publishedAt = computed(() => shortDate(post?.published_at) || (!post && inCanvas ? 'Published date' : ''));
const author = computed(() => post?.author_name || (!post && inCanvas ? 'Author name' : null));
const align = computed(() => (['left', 'center', 'right'].includes(props.config.align) ? props.config.align : 'left'));
</script>

<template>
    <section v-if="(publishedAt || author) && (post || inCanvas)"
             :id="config.anchor_id || null"
             class="th-block th-post-meta"
             :style="styleVars(config)">
        <div class="th-wrap">
            <div class="th-post-meta__inner" :class="`th-post-meta__inner--${align}`">
                <p v-if="publishedAt" class="th-eyebrow th-post-meta__date">{{ publishedAt }}</p>
                <div v-if="author" class="th-post-meta__author">
                    <span class="th-post-meta__avatar">{{ initials(author) }}</span>
                    <span class="th-post-meta__by">By {{ author }}</span>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-post-meta__inner { max-width: 820px; margin: 0 auto; padding-bottom: 20px; }
.th-post-meta__date { margin-bottom: 10px; }
.th-post-meta__author {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px 0;
    border-top: 1px solid var(--th-line);
    border-bottom: 1px solid var(--th-line);
}
.th-post-meta__inner--center { text-align: center; }
.th-post-meta__inner--center .th-post-meta__author { justify-content: center; }
.th-post-meta__inner--right { text-align: right; }
.th-post-meta__inner--right .th-post-meta__author { justify-content: flex-end; }
.th-post-meta__avatar {
    width: 40px;
    height: 40px;
    flex: none;
    border-radius: 50%;
    background: var(--tint);
    color: var(--accent);
    display: grid;
    place-items: center;
    font: 700 13px/1 var(--th-display);
}
.th-post-meta__by { font-weight: 600; font-size: 15px; }
</style>
